import { Button, Typography } from '@breetometer/components';
import { ChevronDown } from 'BreetAssets';
import { Checkbox, DropdownWrapper, EmptyState, Loader, ModalParts } from 'BreetComponents';
import { alwaysRefetchApiQueryOptions, formatDateToReadable, handleApiError, type IPagination, showToast } from 'BreetHelpers';
import {
	closeModal,
	useAppDispatch,
	useDeleteNotificationsMutation,
	useGetNotificationsQuery,
	useMarkNotificationAsReadMutation,
} from 'BreetRedux';
import { type RefObject, useCallback, useState } from 'react';

import { PaginationShort } from '../Pagination/PaginationShort';
import { groupByCreatedAt } from './DialogNotifications.helpers';
import NotificationItem from './DialogNotifications.NotificationItem';

const DialogNotifications = () => {
	const [pageNumber, setPageNumber] = useState<number>(1);

	const {
		isLoading: allNotificationsLoading,
		isFetching: allNotificationsFetching,
		data: allNotificationsData,
	} = useGetNotificationsQuery({ page: pageNumber, size: 20 }, alwaysRefetchApiQueryOptions);

	const [deleteNotifications, { isLoading: deleteNotificationsLoading }] = useDeleteNotificationsMutation();

	const [markNotificationsAsRead, { isLoading: markNotificationsAsReadLoading }] = useMarkNotificationAsReadMutation();

	const dispatch = useAppDispatch();

	const [selectedNotificationIds, setSelectedNotificationIds] = useState<string[]>([]);

	const notificationData = allNotificationsData?.data;

	const groupedNotificationsByDate = groupByCreatedAt(notificationData ?? []);

	const toggleNotificationId = (prevArray: string[], id: string): string[] =>
		prevArray.includes(id) ? prevArray.filter((i) => i !== id) : [...prevArray, id];

	const onSelectNotificationItem = useCallback(
		(id: string) => () => {
			setSelectedNotificationIds((prev) => toggleNotificationId(prev, id));
		},
		[]
	);

	const handleNotificationDeletion = useCallback(
		(_ids: string[], callBack?: () => void) => () => {
			callBack?.();
			const queryString = _ids.map((id) => `_ids=${id}`).join('&');
			deleteNotifications({ body: { _ids }, params: `?${queryString}` })
				.unwrap()
				.then(() => {
					showToast('success', _ids.length > 1 ? 'Selected notifications deleted' : 'Notification deleted');
					setSelectedNotificationIds([]);
				})
				.catch(handleApiError);
		},
		[deleteNotifications]
	);

	const handleReadNotification = useCallback(
		(_ids: string[]) => {
			markNotificationsAsRead({ _ids })
				.unwrap()
				.then(() => {
					if (_ids.length > 1) showToast('success', 'Selected notifications marked as read');
					setSelectedNotificationIds([]);
				})
				.catch(handleApiError);
		},
		[markNotificationsAsRead]
	);

	const handleReadSelectedNotifications = useCallback(
		(callBack: () => void) => () => {
			callBack();
			handleReadNotification(selectedNotificationIds);
		},
		[handleReadNotification, selectedNotificationIds]
	);

	const getHandleReadSingleNotification = useCallback(
		(id: string) => () => handleReadNotification([id]),
		[handleReadNotification]
	);

	const allSelected = selectedNotificationIds.length === notificationData?.length;

	const handleSelectAll = useCallback(() => {
		setSelectedNotificationIds(() => {
			if (allSelected) return [];

			return notificationData?.map((i) => i._id) ?? [];
		});
	}, [allSelected, notificationData]);

	const dropdownWrapper = `notifications_bulkaction${!selectedNotificationIds.length ? ' disabled' : ''}`;

	const bulkActionLoading = deleteNotificationsLoading || markNotificationsAsReadLoading;

	const closeMenu = useCallback(() => {
		dispatch(closeModal('notifications'));
	}, [dispatch]);

	const onPageChange = useCallback((state: Partial<IPagination>) => {
		setPageNumber(state.currentPage ?? 1);
	}, []);

	if (allNotificationsLoading) return <Loader />;

	return (
		<>
			<ModalParts.Wrapper
				title='Notifications'
				handleBackLink={closeMenu}
				rightSheet
			>
				<div className='notifications scrollbar'>
					{!notificationData?.length ? (
						<EmptyState
							title='No notifications yet'
							description="We'll let you know when we've got something new for you."
						/>
					) : (
						<div className='notifications_main'>
							<div className='notifications_controls'>
								<Checkbox
									onChange={handleSelectAll}
									checked={allSelected}
									className='cursor_pointer'
								>
									<span className='color-primary-50'>Select All</span>&nbsp;&nbsp;&nbsp;
									{!!selectedNotificationIds.length && (
										<span className='selected'>{selectedNotificationIds.length} selected</span>
									)}
								</Checkbox>
								<DropdownWrapper>
									{({ buttonRef, controlRef, dropdownRef, dropdownStyles, handleVisibility, visible }) => (
										<div
											ref={controlRef as RefObject<HTMLDivElement>}
											className={dropdownWrapper}
										>
											<Button
												variant='native'
												onClick={selectedNotificationIds.length ? handleVisibility : undefined}
												ref={buttonRef}
												className='cursor_pointer'
											>
												{bulkActionLoading && <div className='button-spinner' />}
												Bulk Action
												<ChevronDown className={`chevron${visible ? ' open' : ''} svg-light`} />
											</Button>
											{visible && (
												<div
													ref={dropdownRef}
													style={dropdownStyles}
													className='notifications_bulkactiondropdown bg-primary-400 border-primary-500'
												>
													<Typography
														as='li'
														className='bg-primary-500-hover'
													>
														<Button
															variant='native'
															onClick={handleReadSelectedNotifications(handleVisibility)}
															className='color-neutral-50'
														>
															Mark selected as read
														</Button>
													</Typography>
													<Typography
														as='li'
														className='bg-primary-500-hover hidden'
													>
														<Button
															variant='native'
															onClick={handleNotificationDeletion(selectedNotificationIds, handleVisibility)}
															className='color-neutral-50'
														>
															Delete selected
														</Button>
													</Typography>
												</div>
											)}
										</div>
									)}
								</DropdownWrapper>
							</div>

							<div className='notifications_content scrollbar'>
								{groupedNotificationsByDate.map((group) => (
									<div
										className='notifications_allmessages'
										key={group.date}
									>
										<div className='notifications_allmessagesgroup'>
											<Typography
												as='label'
												className='color-neutral-50'
											>
												{formatDateToReadable(group.date)}
											</Typography>
											{group.items.map((item) => (
												<NotificationItem
													{...item}
													selected={selectedNotificationIds.includes(item._id)}
													onSelect={onSelectNotificationItem(item._id)}
													deleteItem={handleNotificationDeletion([item._id])}
													markItemAsRead={getHandleReadSingleNotification(item._id)}
													deleteNotificationsLoading={deleteNotificationsLoading}
													key={item._id}
												/>
											))}
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</ModalParts.Wrapper>
			{!!notificationData?.length && (
				<div className='notifications_pagination dropdownshadow bg-primary-500 theme-border-none border-top-dark-navy'>
					<PaginationShort
						currentPage={allNotificationsData?.meta?.page ?? 0}
						pageLimit={20}
						totalDocItems={allNotificationsData?.meta?.totalDocs ?? 0}
						onPageChange={onPageChange}
						isLoading={allNotificationsFetching}
					/>
				</div>
			)}
		</>
	);
};

export default DialogNotifications;
